import { ArrowDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { ReferralOwnerRow, ReferralOwnerType, ReferralSort } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatDate } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { NativeSelect } from '@/shared/ui/native-select.tsx'
import { DesktopTable, MobileCardList } from '@/shared/ui/responsive-table.tsx'
import { referralOwnerPath } from '../filters.ts'
import { ReferralOwnerCard } from './referral-owner-card.tsx'

interface Column {
  title: string
  sort?: ReferralSort
  businessOnly?: boolean
}

const COLUMNS: Column[] = [
  { title: 'Владелец' },
  { title: 'Код' },
  { title: 'Приглашено', sort: 'invited' },
  { title: 'Пользователей', businessOnly: true },
  { title: 'Бизнесов', businessOnly: true },
  { title: 'Активных', sort: 'active' },
  { title: 'Подозрительные' },
  { title: 'Код создан', sort: 'created_at' },
]

const SORT_OPTIONS = COLUMNS.flatMap((column) => (column.sort ? [{ value: column.sort, label: column.title }] : []))

interface ReferralsTableProps {
  rows: ReferralOwnerRow[]
  ownerType: ReferralOwnerType
  sort: ReferralSort
  listSearch: string
  onSort: (sort: ReferralSort) => void
  onOpen: (code: string) => void
}

function Count({ value }: { value: number }) {
  return <span className={cn('tabular', value === 0 && 'text-ink-faint')}>{value}</span>
}

/** Phones have no column headers to tap, so the same sorts sit in a native picker above the cards. */
function SortSelect({ sort, onSort }: { sort: ReferralSort; onSort: (sort: ReferralSort) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 border-b border-line-soft px-4 py-2 text-[13px] text-ink-muted">
      <span className="inline-flex items-center gap-1.5">
        <ArrowDown className="size-3.5" aria-hidden />
        Сортировка
      </span>
      <NativeSelect value={sort} options={SORT_OPTIONS} onChange={onSort} className="w-auto" />
    </label>
  )
}

function HeaderCell({ column, sort, onSort }: { column: Column; sort: ReferralSort; onSort: (sort: ReferralSort) => void }) {
  const active = column.sort === sort

  return (
    <th
      scope="col"
      aria-sort={active ? 'descending' : undefined}
      className="px-3.5 py-3 font-medium whitespace-nowrap"
    >
      {column.sort ? (
        <button
          type="button"
          onClick={() => column.sort && onSort(column.sort)}
          className={cn('inline-flex items-center gap-1 transition-colors hover:text-ink', active && 'text-ink')}
        >
          {column.title}
          <ArrowDown className={cn('size-3', !active && 'opacity-0')} aria-hidden />
        </button>
      ) : (
        column.title
      )}
    </th>
  )
}

export function ReferralsTable({ rows, ownerType, sort, listSearch, onSort, onOpen }: ReferralsTableProps) {
  const columns = COLUMNS.filter((column) => ownerType === 'business' || !column.businessOnly)

  return (
    <>
      <div className="md:hidden">
        <SortSelect sort={sort} onSort={onSort} />
        <MobileCardList label="Реферальные коды">
          {rows.map((row) => (
            <ReferralOwnerCard key={row.code} row={row} ownerType={ownerType} listSearch={listSearch} />
          ))}
        </MobileCardList>
      </div>

      <DesktopTable>
        <table className="w-full min-w-[760px] text-left text-[13.5px]">
          <thead className="border-b border-line-soft text-[12px] text-ink-muted">
            <tr>
              {columns.map((column) => (
                <HeaderCell key={column.title} column={column} sort={sort} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft align-top">
            {rows.map((row) => (
              <tr key={row.code} onClick={() => onOpen(row.code)} className="cursor-pointer hover:bg-surface-hover/50">
                <td className="max-w-[280px] px-3.5 py-3">
                  <Link
                    to={referralOwnerPath(row.code)}
                    state={{ back: listSearch }}
                    onClick={(event) => event.stopPropagation()}
                    className="block truncate font-medium hover:text-brand"
                  >
                    {row.ownerName || row.ownerEmail || '—'}
                  </Link>
                  {row.ownerName && row.ownerEmail && (
                    <div className="mt-0.5 truncate text-[12px] text-ink-muted" title={row.ownerEmail}>
                      {row.ownerEmail}
                    </div>
                  )}
                </td>
                <td className="px-3.5 py-3 font-mono text-[13px] whitespace-nowrap">{row.code}</td>
                <td className="px-3.5 py-3 font-medium">
                  <Count value={row.invited} />
                </td>
                {ownerType === 'business' && (
                  <>
                    <td className="px-3.5 py-3">
                      <Count value={row.users} />
                    </td>
                    <td className="px-3.5 py-3">
                      <Count value={row.businesses} />
                    </td>
                  </>
                )}
                <td className="px-3.5 py-3">
                  <Count value={row.active} />
                </td>
                <td className="px-3.5 py-3">
                  {row.suspicious > 0 ? (
                    <Badge tone="warn">
                      <span className="tabular">{row.suspicious}</span>
                    </Badge>
                  ) : (
                    <span className="text-ink-faint">—</span>
                  )}
                </td>
                <td className="px-3.5 py-3 whitespace-nowrap">{row.createdAt ? formatDate(row.createdAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>
    </>
  )
}
